import Typography from "@mui/joy/Typography";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Cohort, CohortId } from "../../../models/cohort";
import { UnixTimestamp } from "../../../models/common";
import AlertDialogModal from "../../../shared/components/ui/AlertDialogModal";
import UnifiedDataTable, {
  DataTableColumn,
  createDeleteAction,
} from "../../../shared/components/ui/UnifiedDataTable";
import { date_from_timestamp } from "../../../shared/utils/time";

interface CohortListProps {
  cohorts: Cohort[];
  deleteCohort: (ids: CohortId[]) => void;
}

export const CohortList = ({ cohorts, deleteCohort }: CohortListProps) => {
  const navigate = useNavigate();
  const [openDeleteCohort, setOpenDeleteCohort] = React.useState(false);
  const [selectedCohorts, setSelectedCohorts] = React.useState<CohortId[]>([]);
  const [deleting, setDeleting] = React.useState(false);

  function handleDeleteCohort() {
    function reset() {
      setSelectedCohorts([]);
      setOpenDeleteCohort(false);
      setDeleting(false);
    }
    if (selectedCohorts.length > 0) {
      setDeleting(true);
      deleteCohort(selectedCohorts);
      reset();
    } else {
      reset();
    }
  }

  const columns: DataTableColumn<Cohort>[] = [
    {
      key: "name",
      header: "Name",
      width: "40%",
      render: (cohort) => (
        <Typography>
          {cohort.name} {cohort.snapshot_id && <Typography level="body-xs">(frozen)</Typography>}
        </Typography>
      ),
    },
    {
      key: "patients",
      header: "Number of patients",
      render: (cohort) =>
        cohort.subjects && cohort.subjects.length !== 0
          ? cohort.subjects.length + " patients"
          : "0 patient",
    },
    {
      key: "updated",
      header: "Last updated date",
      render: (cohort) => date_from_timestamp(cohort.update_date as UnixTimestamp),
    },
  ];

  const actions = [
    createDeleteAction((selectedCohorts: Cohort[]) => {
      setSelectedCohorts(selectedCohorts.map((c) => c.cohort_id));
      setOpenDeleteCohort(true);
    }),
  ];

  const emptyState = {
    title: "No cohort here yet",
  };

  return (
    <>
      <AlertDialogModal
        alertMessage="Are you sure you want to delete this cohort?"
        open={openDeleteCohort}
        setOpen={setOpenDeleteCohort}
        handleDelete={handleDeleteCohort}
        loading={deleting}
      />

      <UnifiedDataTable
        data={cohorts}
        columns={columns}
        getItemId={(cohort) => cohort.cohort_id}
        selectable={true}
        selectedItems={selectedCohorts}
        onSelectionChange={(newSelected) => setSelectedCohorts(newSelected as CohortId[])}
        actions={actions}
        onRowClick={(cohort) => navigate(`/cohort/${cohort.cohort_id}`)}
        pageSize={50}
        paginationThreshold={50}
        ariaLabel="cohort table"
        emptyState={emptyState}
      />
    </>
  );
};
